import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ExampleCard from '../../cards/ExampleCard';
import SendMessageInput from '../../ui/SendMessageInput';
import InvalidFileDialog from '../../modals/InvalidFileDialog';
import RenameProjectDialog from '../../modals/RenameProjectDialog';
import DeleteModal from '../../modals/DeleteModal';
import ExportModal from '../../modals/ExportModal';
import Toast from '../../ui/Toast';
import ProjectGrid from '../../ui/ProjectGrid';
import EditIcon from '../../icons/EditIcon';
import { Project, ExportQueryData } from '../../../types/project';
import { streamingAPI, StreamingQueryResponse } from '../../../services/streamingApi';
import { projectsAPI } from '../../../services/projectsApi';
import SparkleIcon from '../../icons/SparkleIcon';
import SustainabilityIcon from '../../icons/SustainabilityIcon';
import BullsEyeIcon from '../../icons/BullsEyeIcon';
import TrendArrowIcon from '../../icons/TrendArrowIcon';

interface ExampleQuestion {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}

interface NewDashboardViewProps {
  project?: Project;
  isCreating: boolean;
  projects?: Project[];
  title?: string;
  projectName?: string;
  onBack: () => void;
  onSave?: (project: Partial<Project>) => void;
  onCreateWithQuestion?: (name: string, question: string, attachedFiles: File[]) => Promise<void>;
  onViewProject?: (project: Project) => void;
  onExportProject?: (project: Project) => void;
  onRenameProject?: (project: Project, newName: string) => Promise<void>;
  onDeleteProject?: (project: Project) => void;
  onRenameProjectName?: (newName: string) => void;
  onResetProject?: () => void;
  isAgentMode?: boolean;
  onAgentModeChange?: (value: boolean) => void;
}

const NewDashboardView: React.FC<NewDashboardViewProps> = ({
  title = 'Create New Project',
  projects = [],
  project,
  isCreating,
  projectName = '',
  onBack,
  onSave,
  onCreateWithQuestion,
  onViewProject,
  onExportProject,
  onRenameProject,
  onDeleteProject,
  onRenameProjectName,
  onResetProject,
  isAgentMode = true,
  onAgentModeChange
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [question, setQuestion] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showInvalidFileDialog, setShowInvalidFileDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [projectToRenameFromGrid, setProjectToRenameFromGrid] = useState<Project | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [projectToExport, setProjectToExport] = useState<ExportQueryData | undefined>(undefined);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [toastAction, setToastAction] = useState<{ label: string; action: () => void } | undefined>(undefined);

  // Check if projectName is provided
  useEffect(() => {
    // If no project name provided, redirect to dashboard
    if ((!projectName || projectName === '') && isCreating) {
      navigate('/dashboard', { replace: true });
    }
  }, [projectName, isCreating, navigate]);

  const handleRenameClick = () => {
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = async (newName: string) => {
    // Renaming the current project name (header rename)
    if (projectToRenameFromGrid && onRenameProject) {
      // Renaming an existing project from the grid
      try {
        await onRenameProject(projectToRenameFromGrid, newName);
        setToastMessage('Project has been renamed successfully!');
        setToastType('success');
        setToastAction(undefined);
        setToastVisible(true);
        setRenameDialogOpen(false);
        setProjectToRenameFromGrid(null);
      } catch (error) {
        setToastMessage('Renaming project was unsuccessful due to a technical issue.');
        setToastType('error');
        setToastAction({
          label: 'Retry',
          action: () => {
            setToastVisible(false);
            setRenameDialogOpen(true);
          }
        });
        setToastVisible(true);
      }
    } else if (onRenameProjectName) {
      // Renaming the new project name being created
      onRenameProjectName(newName);
      setRenameDialogOpen(false);
    }
  };

  const handleRenameCancel = () => {
    setRenameDialogOpen(false);
    setProjectToRenameFromGrid(null);
  };

  const handleRenameProjectFromGrid = (project: Project) => {
    setProjectToRenameFromGrid(project);
    setRenameDialogOpen(true);
  };

  const handleDeleteProject = (project: Project) => {
    setProjectToDelete(project);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete || !onDeleteProject) return;

    setIsDeleting(true);
    try {
      await onDeleteProject(projectToDelete);
      setDeleteModalOpen(false);
      const deletedProjectName = projectToDelete.name;
      setProjectToDelete(null);
      setToastMessage(`"${deletedProjectName}" has been successfully deleted`);
      setToastType('success');
      setToastVisible(true);
    } catch (error) {
      console.error('Failed to delete project:', error);
      setToastMessage('Failed to delete project. Please try again.');
      setToastType('error');
      setToastVisible(true);
      setDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setProjectToDelete(null);
    setIsDeleting(false);
  };

  const handleExportProject = async (project: Project) => {
    let conversationHistory = project.conversation_history || [];
    let currentProject: Project;

    // If project has conversation history, try to fetch the full query result
    if (!conversationHistory.length) {
      currentProject = await projectsAPI.getProject(project.project_id);
      conversationHistory = currentProject.conversation_history || [];
    }

    if (conversationHistory.length > 0) {
      try {
        // Get the last conversation entry with a query_id
        const lastEntry = conversationHistory[conversationHistory.length - 1];
        if (lastEntry.query_id) {
          // Fetch the full result to get proper export data
          let fullResult = await streamingAPI.fetchQueryFullResult(lastEntry.query_id, project.initial_query || '');

          if (!fullResult) {
            fullResult = lastEntry.result as StreamingQueryResponse;
          }

          // Store the full result data for export
          setProjectToExport({
            ...project,
            query: project.initial_query || '',
            ...fullResult
          });
        } else {
          // Fall back to using project data with initial_query as query
          setProjectToExport({
            ...project,
            query: project.initial_query || ''
          });
        }
      } catch (error) {
        console.error('Failed to fetch query result for export:', error);
        // Fall back to using project data with initial_query as query
        setProjectToExport({
          ...project,
          query: project.initial_query || ''
        });
      }
      setExportModalOpen(true);
    } else {
      // Fall back to using project data with initial_query as query
      setToastMessage("Something went wrong. Failed to load project export data");
      setToastType('error');
      setToastVisible(true);
    }
  };

  const handleExportConfirm = async (options: any, error?: string) => {
    // Show appropriate toast based on export result
    if (error) {
      setToastMessage(error);
      setToastType('error');
      setToastVisible(true);
    } else if (options.format === 'pdf') {
      setToastMessage('PDF report exported successfully');
      setToastType('success');
      setToastVisible(true);
    } else if (options.format === 'powerpoint') {
      setToastMessage('PowerPoint presentation exported successfully');
      setToastType('success');
      setToastVisible(true);
    }

    setExportModalOpen(false);
    setProjectToExport(undefined);
  };

  const handleExportCancel = () => {
    setExportModalOpen(false);
    setProjectToExport(undefined);
  };

  const exampleQuestions: ExampleQuestion[] = [
    {
      icon: <TrendArrowIcon size={48} />,
      title: "Market Positioning Analysis",
      subtitle: "Can you run a structured positioning analysis across market, financial, strategy moves, and regulation?"
    },
    {
      icon: <BullsEyeIcon size={48} />,
      title: "Financial Insight Analysis",
      subtitle: "Produce board ready analysis with insights."
    },
    {
      icon: <SustainabilityIcon size={48} />,
      title: "Sustainable Finance Opportunities",
      subtitle: "What's the ROI potential for entering sustainable finance products?"
    }
  ];


  const allowedFileTypes = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.csv', '.xls', '.xlsx', '.json'];
  const allowedMimeTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/json'
  ];

  const isValidFile = (file: File) => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    return allowedFileTypes.includes(fileExtension) || allowedMimeTypes.includes(file.type);
  };

  const addFiles = (files: File[]) => {
    const validFiles = files.filter(isValidFile);
    const invalidFiles = files.filter(file => !isValidFile(file));

    // Show invalid file dialog if there are invalid files
    if (invalidFiles.length > 0) {
      setShowInvalidFileDialog(true);
      return; // Don't add any files if there are invalid ones
    }

    const newFiles = validFiles.filter(file =>
      !attachedFiles.some(existingFile =>
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified
      )
    );

    if (newFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }

    const duplicateCount = validFiles.length - newFiles.length;

    if (duplicateCount > 0) {
      console.warn(`${duplicateCount} duplicate file(s) were skipped.`);
    }
  };

  const onSelectExample = (example: ExampleQuestion) => {
    setQuestion(example.subtitle);
    // Only update project name if project hasn't been created yet
    if (onRenameProjectName && !project) {
      onRenameProjectName(example.title);
    }
  };

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.doc,.docx,.ppt,.pptx,.csv,.xls,.xlsx,.json';

    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      addFiles(files);
    };

    input.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const files = items
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter((file): file is File => file !== null);

    if (files.length > 0) {
      addFiles(files);
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleQuestionSubmit = async () => {
    if (question.trim() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        if (project) {
          // Update existing project with query details
          await projectsAPI.updateProject(project.project_id, {
            description: question,
            initial_query: question,
            metadata: {
              ...project.metadata,
              attachedFiles: attachedFiles.map(f => ({ name: f.name, size: f.size, type: f.type }))
            }
          });
          // Navigate to chat view for existing project
          console.log('[NewDashboardView] Navigating with isAgentMode:', isAgentMode);
          navigate(`/dashboard/project/${project.project_id}`, {
            state: {
              initialQuestion: question,
              initialAttachments: attachedFiles,
              autoSubmitQuery: true // Flag to indicate automatic query submission
            }
          });

          setTimeout(() => {
            // Reset project state in parent
            if (onResetProject) {
              onResetProject();
            }
          }, 500);

        } else if (onCreateWithQuestion) {
          // Create new project with question
          // Clear project name before creating
          if (onRenameProjectName) {
            onRenameProjectName('');
          }
          await onCreateWithQuestion(projectName, question, attachedFiles);
        }
      } catch (error) {
        console.error('Error creating project:', error);
        // Handle error - could show a toast notification or error message
      } finally {
        setIsSubmitting(false);
      }
    }
  };


  return (
    <main className="dashboard-content">
      <header className="dashboard-header">
        <div className="header-top">
          <div className="breadcrumb">
            <div style={{ display: 'flex' }}>
              <h1 className="main-title">
                {title} {isCreating && projectName && (<span>/ {projectName}</span>)}
              </h1>
              {isCreating && projectName && <button
                onClick={handleRenameClick}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  marginLeft: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Rename project"
              >
                <EditIcon size={20} />
              </button>}
            </div>
          </div>
        </div>
      </header>

      <section className="project-hero-section">
        <div className="hero-content">
          <div
            className={`drag-drop-area ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onPaste={handlePaste}
            tabIndex={0}
          >
            <h2 className="hero-title">What's your focus today?</h2>
            <p className="hero-subtitle">Ask any question to get real-time strategic insights</p>

            <SendMessageInput
              value={question}
              onChange={setQuestion}
              onSubmit={handleQuestionSubmit}
              attachedFiles={attachedFiles}
              onFileSelect={handleFileSelect}
              onFileRemove={removeFile}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onPaste={handlePaste}
              isDragOver={isDragOver}
              variant="default"
              disabled={isSubmitting}
              isAgentMode={isAgentMode}
              onAgentModeChange={(newMode) => {
                console.log('[NewDashboardView] Agent mode changed:', newMode);
                if (onAgentModeChange) {
                  onAgentModeChange(newMode);
                }
              }}
            />
          </div>

          <div className="examples-section">
            <h3 className="examples-title">
              <SparkleIcon size={20} />
              PROJECT EXAMPLES
            </h3>
            <div className="examples-grid">
              {exampleQuestions.map((example, index) => (
                <ExampleCard
                  key={index}
                  icon={example.icon}
                  title={example.title}
                  subtitle={example.subtitle}
                  onClick={() => onSelectExample(example)}
                />
              ))}
            </div>
          </div>

          {projects.length > 0 && onViewProject && (
            <>
              <div className="recent-projects-section">
                <ProjectGrid
                  projects={projects.slice(0, 6)}
                  sectionTitle="EXISTING PROJECTS"
                  showSearch={false}
                  onViewProject={onViewProject}
                  onExport={handleExportProject}
                  onRename={handleRenameProjectFromGrid}
                  onDelete={handleDeleteProject}
                />
              </div>

              {projects.length > 6 && (
                <div className="show-more" onClick={() => navigate('/dashboard/projects')} style={{ cursor: 'pointer' }}>
                  <p>Show All Projects</p>
                  <svg width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.1777 6.35156C15.1777 6.59635 15.0866 6.8099 14.9043 6.99219L9.74805 12.1328C9.56576 12.3099 9.36003 12.3984 9.13086 12.3984C8.89128 12.3984 8.69076 12.3203 8.5293 12.1641C8.37305 12.0078 8.29492 11.8125 8.29492 11.5781C8.29492 11.4583 8.31576 11.3464 8.35742 11.2422C8.4043 11.138 8.4668 11.0469 8.54492 10.9688L10.2949 9.20312L13.209 6.54688L13.4824 7.04688L10.7715 7.20312H1.2793C1.01888 7.20312 0.807943 7.125 0.646484 6.96875C0.490234 6.80729 0.412109 6.60156 0.412109 6.35156C0.412109 6.09635 0.490234 5.89062 0.646484 5.73438C0.807943 5.57292 1.01888 5.49219 1.2793 5.49219H10.7715L13.4824 5.65625L13.209 6.16406L10.2949 3.5L8.54492 1.72656C8.4668 1.64844 8.4043 1.5599 8.35742 1.46094C8.31576 1.35677 8.29492 1.24219 8.29492 1.11719C8.29492 0.882812 8.37305 0.6875 8.5293 0.53125C8.69076 0.375 8.89128 0.296875 9.13086 0.296875C9.24544 0.296875 9.35482 0.320312 9.45898 0.367188C9.56315 0.414062 9.66211 0.484375 9.75586 0.578125L14.9043 5.71094C15.0866 5.89323 15.1777 6.10677 15.1777 6.35156Z" fill="url(#paint0_linear_23_10457)" />
                    <defs>
                      <linearGradient id="paint0_linear_23_10457" x1="-1.70508" y1="6.5" x2="17.2949" y2="6.5" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#00D650" />
                        <stop offset="1" stopColor="#0081FE" />
                      </linearGradient>
                    </defs>
                  </svg>

                </div>)}
            </>
          )}

        </div>
      </section>

      {/* Invalid File Dialog */}
      <InvalidFileDialog
        isOpen={showInvalidFileDialog}
        onClose={() => setShowInvalidFileDialog(false)}
      />

      {/* Rename Project Dialog */}
      {renameDialogOpen && (
        <RenameProjectDialog
          isOpen={renameDialogOpen}
          onClose={handleRenameCancel}
          onRename={handleRenameConfirm}
          projectName={projectToRenameFromGrid ? projectToRenameFromGrid.name : projectName}
          dialogTitle="Rename Project"
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={projectToDelete?.name || ''}
        itemType="project"
        isDeleting={isDeleting}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={exportModalOpen}
        onClose={handleExportCancel}
        onExport={handleExportConfirm}
        queryData={projectToExport}
        projectName={projectToExport?.name}
      />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
        type={toastType}
        actionLabel={toastAction?.label}
        onAction={toastAction?.action}
      />
    </main>
  );
};

export default NewDashboardView;